import React, { useState } from "react";
import { FiDownloadCloud } from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa";
import ColumnMapper from "./parts/ColumnMapper2";
import axiosClient from "../utils/apiClient.js";
import { toast } from "react-toastify";

const dbFields = [
  // CLIENT CORE
  { value: "first_name", label: "Contact First Name" },
  { value: "last_name", label: "Contact Last Name" },
  { value: "email", label: "Primary Email" },
  { value: "phone_number", label: "Phone Number" },
  { value: "agency", label: "Agency / Company" },
  { value: "contact_person", label: "Primary Contact" },
  { value: "billing_address", label: "Billing Address" },
  { value: "state_or_province", label: "State" },
  { value: "county_or_region", label: "County / Region" },

  // CONTACTS (max 5)
  { value: "contacts[0].name", label: "Contact 1 Name" },
  { value: "contacts[0].email", label: "Contact 1 Email" },
  { value: "contacts[0].phone", label: "Contact 1 Phone" },

  { value: "contacts[1].name", label: "Contact 2 Name" },
  { value: "contacts[1].email", label: "Contact 2 Email" },
  { value: "contacts[1].phone", label: "Contact 2 Phone" },

  { value: "contacts[2].name", label: "Contact 3 Name" },
  { value: "contacts[2].email", label: "Contact 3 Email" },
  { value: "contacts[2].phone", label: "Contact 3 Phone" },

  { value: "contacts[3].name", label: "Contact 4 Name" },
  { value: "contacts[3].email", label: "Contact 4 Email" },
  { value: "contacts[3].phone", label: "Contact 4 Phone" },

  { value: "contacts[4].name", label: "Contact 5 Name" },
  { value: "contacts[4].email", label: "Contact 5 Email" },
  { value: "contacts[4].phone", label: "Contact 5 Phone" },
];


const ExportClients = () => {
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [exportMode, setExportMode] = useState("default"); // default | custom

  const resetState = () => {
    setMapping({});
    setExportMode("default");
  };

  const updateMapping = (dbField, csvHeader) => {
    setMapping((prev) => ({ ...prev, [dbField]: csvHeader }));
  };

  // ------------------------
  // Download Helper
  // ------------------------
  const downloadCSV = async (response, filename) => {
    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ------------------------
  // Default Export
  // ------------------------
  const exportDefault = async () => {
    try {
      setLoading(true);

      const res = await axiosClient.get("/clients/export/csv", {
        responseType: "blob",
      });

      await downloadCSV(res, "clients_default_export.csv");
    } catch (err) {
      console.error("Default export failed:", err);
      toast.error("Failed to export CSV.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------
  // Custom Export
  // ------------------------
  const exportCustom = async () => {
    try {
      setLoading(true);

      const columns = Object.entries(mapping)
        .filter(([_, header]) => header && header.trim() !== "")
        .map(([field, header]) => ({
          header,
          field,
        }));

      if (!columns.length) {
        toast.warning("Please map at least one column.");
        setLoading(false);
        return;
      }

      const res = await axiosClient.post(
        "/clients/export/csv/custom",
        { columns },
        { responseType: "blob" }
      );

      await downloadCSV(res, "clients_custom_export.csv");
    } catch (err) {
      console.error("Custom export failed:", err);
      toast.error("Failed to export CSV.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-sky-700">Export Clients</h1>
        <button
          onClick={resetState}
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          disabled={loading}
        >
          Reset
        </button>
      </div>

      {/* Mode Selection */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setExportMode("default")}
          className={`px-4 py-2 rounded-lg font-semibold ${
            exportMode === "default"
              ? "bg-sky-700 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Default Export
        </button>

        <button
          onClick={() => setExportMode("custom")}
          className={`px-4 py-2 rounded-lg font-semibold ${
            exportMode === "custom"
              ? "bg-sky-700 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Custom Export
        </button>
      </div>

      {/* Default Export Panel */}
      {exportMode === "default" && (
        <div className="border border-sky-200 bg-sky-50 p-8 rounded-xl flex flex-col items-center gap-3">
          <FiDownloadCloud className="text-5xl text-sky-600" />
          <h2 className="text-xl font-semibold text-sky-800">
            Export All Clients (Default Format)
          </h2>
          <p className="text-gray-600 text-sm text-center max-w-md">
            Download all clients in the system’s default CSV structure including
            up to 5 contacts per client.
          </p>

          <button
            onClick={exportDefault}
            disabled={loading}
            className="mt-4 px-6 py-3 bg-sky-700 text-white font-bold rounded-lg flex items-center gap-2 hover:bg-sky-800 transition"
          >
            {loading ? "Preparing..." : "Download CSV"} <FaArrowRight />
          </button>
        </div>
      )}

      {/* Custom Export Panel */}
      {exportMode === "custom" && (
        <div className="mt-8">
          <h2 className="text-2xl font-semibold mb-2">Custom Column Mapping</h2>
          <p className="text-sm mb-4 text-gray-600">
            Choose which fields you want in your CSV and define the column headers.
          </p>

          <div className="grid gap-3">
            {dbFields.map((field) => (
              <ColumnMapper
                key={field.value}
                dbField={field}
                csvColumns={[]} // not needed here, we provide custom header input
                selected={mapping[field.value] || ""}
                onChange={updateMapping}
                isExport
              />
            ))}
          </div>

          <div className="mt-6">
            <button
              onClick={exportCustom}
              disabled={loading}
              className="px-6 py-3 bg-sky-700 text-white font-bold rounded-lg flex items-center gap-2 hover:bg-sky-800 transition"
            >
              {loading ? "Generating CSV..." : "Export Custom CSV"} <FaArrowRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportClients;
